import { useEffect, useState } from 'react';
import { configApi } from '@/api/config';
import { sitesApi } from '@/api/sites';
import { alerts as alertsApi } from '@/api/alerts';

export type HealthState = 'ok' | 'warning' | 'error';

export interface SystemHealth {
  settingsStatus: HealthState;
  sitesStatus: HealthState;
  alertCount: number;
  loading: boolean;
  error: string | null;
}

// Required integration keys that gate first-run readiness. Mirrors the set
// surfaced by SetupChecklist in Phase W1 so the sidebar dot matches the
// onboarding state.
const REQUIRED_KEYS = [
  'anthropic_api_key',
  'cloudflare_api_token',
  'cloudflare_account_id',
  'github_owner',
  'github_repo',
];

const INITIAL: SystemHealth = {
  settingsStatus: 'ok',
  sitesStatus: 'ok',
  alertCount: 0,
  loading: true,
  error: null,
};

export function useSystemHealth(pollIntervalMs = 30000): SystemHealth {
  const [state, setState] = useState<SystemHealth>(INITIAL);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async () => {
      const [configRes, sitesRes, alertsRes] = await Promise.all([
        configApi.list().then(
          (entries) => ({ ok: true as const, entries }),
          (err: unknown) => ({ ok: false as const, err }),
        ),
        sitesApi.list().then(
          (sites) => ({ ok: true as const, sites }),
          (err: unknown) => ({ ok: false as const, err }),
        ),
        alertsApi
          .listGlobal({ severity: 'error', unread: true })
          .then(
            (rows) => ({ ok: true as const, rows }),
            (err: unknown) => ({ ok: false as const, err }),
          ),
      ]);
      if (cancelled) return;

      let settingsStatus: HealthState = 'ok';
      if (!configRes.ok) {
        settingsStatus = 'warning';
      } else {
        const byKey = new Map(configRes.entries.map((e) => [e.key, e]));
        const requiredEntries = REQUIRED_KEYS.map((k) => byKey.get(k));
        const failedTest = requiredEntries.some(
          (e) => e && e.configured && e.last_test_ok === false,
        );
        const unconfigured = requiredEntries.some((e) => !e || !e.configured);
        if (failedTest) settingsStatus = 'error';
        else if (unconfigured) settingsStatus = 'warning';
      }

      let sitesStatus: HealthState = 'ok';
      if (!sitesRes.ok) {
        sitesStatus = 'warning';
      } else {
        const hasFailedDomain = sitesRes.sites.some(
          (s) => s.status === 'active' && s.domain_status === 'failed',
        );
        const hasFailedScaffold = sitesRes.sites.some(
          (s) => s.status === 'decommissioned',
        );
        const hasScaffolding = sitesRes.sites.some(
          (s) => s.status === 'scaffolding',
        );
        if (hasFailedDomain || hasFailedScaffold) sitesStatus = 'error';
        else if (hasScaffolding) sitesStatus = 'warning';
      }

      const alertCount = alertsRes.ok ? alertsRes.rows.length : 0;

      setState({
        settingsStatus,
        sitesStatus,
        alertCount,
        loading: false,
        error: null,
      });

      if (!cancelled) {
        timer = setTimeout(tick, pollIntervalMs);
      }
    };

    void tick();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [pollIntervalMs]);

  return state;
}
